import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Plus, Star, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

const Stars = ({ rating }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3 h-3 ${i <= rating ? "fill-[#ff4500] text-[#ff4500]" : "text-[#ddd]"}`} strokeWidth={1.5} />
    ))}
  </span>
);

/**
 * Per-campsite Wall strip — shows up to 3 most recent trip stories for this site
 * plus a primary CTA to share a new trip. Designed to slot into CampsiteDetail
 * to (a) bring the page to life and (b) drive Wall participation.
 */
export default function WallStripForCampsite({ campsiteId, campsiteName }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/wall", { params: { campsite_id: campsiteId, limit: 3 } });
        if (alive) setStories(data.items || []);
      } catch {} finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [campsiteId]);

  return (
    <section className="border hairline bg-white p-5" data-testid="campsite-wall-strip">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#ff4500]" strokeWidth={1.75} />
          <h3 className="font-display text-lg leading-none">From the Wall</h3>
        </div>
        <Link to={`/wall?new=1`} className="text-xs mono link-underline text-[#ff4500] inline-flex items-center gap-1" data-testid="campsite-wall-share">
          <Plus className="w-3 h-3" strokeWidth={2.5} /> Share your trip
        </Link>
      </div>

      {loading ? (
        <div className="text-xs mono text-[#888]">Loading…</div>
      ) : stories.length === 0 ? (
        <div className="text-sm leading-snug text-[#555] flex items-start gap-2 bg-[#fafafa] p-4 border hairline">
          <Camera className="w-5 h-5 text-[#ff4500] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <span>
            No trips shared for {campsiteName} yet. <Link to="/wall?new=1" className="font-bold link-underline text-[#ff4500]">Be the first</Link> — your photos could even feature on this page.
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          {stories.map((s) => {
            const cover = s.photos?.[0]?.image_data_url;
            return (
              <article key={s.story_id} className="flex gap-3 border hairline p-3 hover:bg-[#fffdfa]" data-testid={`campsite-wall-story-${s.story_id}`}>
                {cover ? (
                  <img src={cover} alt="" className="w-20 h-20 object-cover flex-shrink-0 border hairline" loading="lazy" />
                ) : (
                  <div className="w-20 h-20 bg-[#fff4ee] flex items-center justify-center flex-shrink-0 border hairline">
                    <Camera className="w-5 h-5 text-[#ff4500]/50" strokeWidth={1.5} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] mono text-[#666] truncate">— {s.user_name}</span>
                    <Stars rating={s.rating} />
                  </div>
                  <p className="text-xs leading-snug text-[#222] line-clamp-3">{s.highlight}</p>
                </div>
              </article>
            );
          })}
          <Link to="/wall" className="text-xs mono text-[#ff4500] link-underline inline-flex items-center gap-1" data-testid="campsite-wall-all">
            See all trips on the Wall <ArrowRight className="w-3 h-3" strokeWidth={2} />
          </Link>
        </div>
      )}
    </section>
  );
}
